import { useMemo } from 'react';
import { useUserOptionRender } from '@src/hooks';
import {
  FOLLOW_UP_TYPE_OPTIONS,
  FOLLOW_UP_STATUS_OPTIONS,
  FOLLOW_UP_OUTCOME_OPTIONS,
  LEAD_OUTCOME_OPTIONS,
  FEEDBACK_OUTCOME_OPTIONS,
  APPOINTMENT_OUTCOME_OPTIONS,
} from '../constants';
import { cleanPhoneNumber, getPhoneFieldConfig } from '@src/utils/phoneUtils';
import { useReferenceDetails } from './useReferenceDetails';
import { useOfferingsInsights } from './useOfferingsInsights';
import { DateTimePickerField, SelectField } from '@src/components/shared/fields';
import dayjs from 'dayjs';

export const useFollowUpsFormFields = (record?: any) => {
  const userOptionRender = useUserOptionRender({
    allowedUserTypes: [
      'admin',
      'doctor',
      'therapist',
      'counsellor',
      'receptionist',
    ],
    showUserType: true,
    size: 'small',
  });

  // Check if this is an edit operation (record exists and has an ID)
  const isEditMode = record && record.id;

  // Function to get outcome options based on type
  const getOutcomeOptions = (type: string) => {
    switch (type) {
      case 'LEAD':
        return LEAD_OUTCOME_OPTIONS;
      case 'FEEDBACK':
        return FEEDBACK_OUTCOME_OPTIONS;
      case 'APPOINTMENT':
        return APPOINTMENT_OUTCOME_OPTIONS;
      default:
        return FOLLOW_UP_OUTCOME_OPTIONS;
    }
  };

  return useMemo(
    () => [
      // Row 1 - Type & Reference ID
      {
        name: 'type',
        label: 'Follow-up Type',
        type: 'customAutoComplete' as const,
        required: true,
        placeholder: 'Select or type follow-up type',
        layout: { span: 5 },
        options: FOLLOW_UP_TYPE_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
          allowClear: true,
          disabled: isEditMode,
        },
        tooltip:
          'Select from predefined options or type a custom follow-up type',
        transform: (value: string) => {
          if (!value) return value;
          return value.toUpperCase().replace(/\s+/g, '_');
        },
      },
      {
        name: 'reference_id',
        label: 'Reference ID',
        type: 'number' as const,
        required: false,
        placeholder: 'Enter reference ID',
        layout: { span: 5 },
        props: {
          size: 'large',
          min: 1,
          step: 1,
          disabled: isEditMode,
        },
        tooltip:
          'Enter the ID of the related record (appointment, patient, lead, or treatment)',
      },

      // Row 2 - Patient Name & Phone
      {
        name: 'patient_name',
        label: 'Patient Name',
        type: 'text' as const,
        required: false,
        placeholder: 'Enter patient name',
        layout: { span: 7 },
        props: {
          size: 'large',
          disabled: isEditMode,
        },
        tooltip: 'Enter the patient name for this follow-up',
      },
      {
        name: 'patient_phone',
        label: 'Patient Phone',
        ...getPhoneFieldConfig(false, '98765 43210'),
        layout: { span: 7 },
        tooltip: 'Enter patient phone number (without country code)',
      },

      // Row 3 - Follow-up Date & Status
      {
        name: 'follow_up_date',
        label: 'Follow-up Date & Time',
        type: 'datetime' as const,
        required: true,
        placeholder: 'When should the follow-up happen?',
        layout: { span: 8 },
        defaultValue: (() => {
          const now = dayjs();
          const minutes = now.minute();
          const roundedMinutes = Math.round(minutes / 15) * 15;
          let defaultTime = now.minute(roundedMinutes).second(0).millisecond(0);

          // If the rounded time is before 9 AM, set it to 9 AM today
          if (defaultTime.hour() < 9) {
            defaultTime = defaultTime.hour(9).minute(0);
          }
          // If the rounded time is after 8 PM, set it to 9 AM tomorrow
          else if (defaultTime.hour() >= 20) {
            defaultTime = defaultTime.add(1, 'day').hour(9).minute(0);
          }

          return defaultTime;
        })(),
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabled: isEditMode, // Disabled only when editing existing record
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          placeholder: 'Select date and time',
        },
        tooltip: 'Set the date and time for the follow-up',
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select' as const,
        required: true,
        defaultValue: 'PENDING',
        placeholder: 'Select status',
        layout: { span: 8 },
        options: FOLLOW_UP_STATUS_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Current status of this follow-up',
      },

      // Row 4 - Outcome & Attempt Count
      {
        name: 'outcome',
        label: 'Outcome',
        type: 'select' as const,
        required: false,
        placeholder: 'Select outcome',
        layout: { span: 8 },
        options: record?.type ? getOutcomeOptions(record.type) : FOLLOW_UP_OUTCOME_OPTIONS,
        props: {
          size: 'large',
          showSearch: true,
          optionFilterProp: 'children',
        },
        tooltip: 'Result of the follow-up call/meeting',
        defaultValue: null,
        onChange: (value: any, form: any) => {
          // Convert undefined/empty to null for database and clear referred_by_name when type is cleared
          if (value === undefined || value === '') {
            form.setFieldsValue({ outcome: null });
          } else {
                  // When an outcome is selected, automatically set status to DONE
                    form.setFieldsValue({ status: 'DONE' });
                  }
        },
      },
      
      // Row 4.5 - New Follow-up Date (conditional based on outcome)
      {
        name: 'new_follow_up_date',
        label: 'New Follow-up Date & Time',
        type: 'datetime' as const,
        required: false,
        placeholder: 'Select new follow-up date and time',
        layout: { span: 12 },
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          placeholder: 'Select new follow-up date and time',
        },
        tooltip: 'Schedule a new follow-up date when callback is needed',
        hidden: true, // Hide by default - will be shown conditionally via form logic
      },
      
      // Row 4.6 - New Appointment Schedule Date (conditional based on type=APPOINTMENT and outcome=RESCHEDULED)
      {
        name: 'new_appointment_schedule_date',
        label: 'New Appointment Schedule Date & Time',
        type: 'datetime' as const,
        required: false,
        placeholder: 'Select new appointment schedule date and time',
        layout: { span: 12 },
        props: {
          size: 'large',
          showTime: true,
          showShortcuts: true,
          format: 'YYYY-MM-DD hh:mm A',
          showNow: true,
          showToday: true,
          minuteStep: 15,
          use12Hours: true,
          disabledDate: (current: any) => {
            return current && current < dayjs().startOf('day');
          },
          placeholder: 'Select new appointment schedule date and time',
        },
        tooltip: 'Schedule a new appointment date when rescheduling is needed',
        hidden: true, // Hide by default - will be shown conditionally via form logic
      },
      
      // Row 5 - Remark & Assigned To
      {
        name: 'remark',
        label: 'Remark',
        type: 'textarea' as const,
        required: false,
        placeholder: 'Add any important notes for follow-up...',
        layout: { span: 12 },
        props: {
          rows: 3,
          showCount: true,
          maxLength: 500,
        },
        tooltip: 'Additional notes or comments for this follow-up',
      },

      // Suggested Offerings + Previous Offerings (Appointment only, when confirmed)
      {
        name: 'offerings_insights',
        type: 'custom' as const,
        layout: { span: 24 },
        render: (value: any, record: any, form: any) => {
          if (!form || typeof form.getFieldValue !== 'function') {
            return null;
          }

          const type = form.getFieldValue('type');
          const referenceId = form.getFieldValue('reference_id');
          const outcome = form.getFieldValue('outcome');

          // Only show when appointment follow-up is confirmed
          const enabled =
            type === 'APPOINTMENT' && !!referenceId && outcome === 'CONFIRM';

          if (!enabled) return null;

          const { renderDetails } = useOfferingsInsights({
            appointmentId: referenceId || '',
            enabled,
          });

          return renderDetails();
        },
        dependencies: ['type', 'reference_id', 'outcome'],
        required: false,
      },

      // Dynamic Reference Details Component
      {
        name: 'reference_details',
        type: 'custom' as const,
        layout: { span: 24 },
        render: (value: any, record: any, form: any) => {
          if (!form || typeof form.getFieldValue !== 'function') {
            return null;
          }

          const type = form.getFieldValue('type');
          const referenceId = form.getFieldValue('reference_id');
          
          // Only show details if both type and reference ID are provided
          if (!type || !referenceId) {
            return null;
          }
          
          const { renderDetails } = useReferenceDetails({ 
            type: type || '', 
            referenceId: referenceId || '' 
          });

          return renderDetails();
        },
        dependencies: ['type', 'reference_id'], // Re-render when these fields change
        required: false, // This is display-only, not a form field
      },

      // Hidden Attempt Count Field
      {
        name: 'attempt_count',
        label: 'Attempt Count',
        type: 'number' as const,
        required: false,
        placeholder: 'Enter attempt count',
        layout: { span: 0 }, // Hidden layout
        defaultValue: 1,
        props: {
          size: 'large',
          min: 1,
          step: 1,
          style: { display: 'none' },
        },
        tooltip: 'Number of follow-up attempts',
        hidden: true, // Always hidden from UI
      },
    ],
    [userOptionRender, record]
  );
};
